import { useEffect, useState } from "react";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Sparkles, Bed, Wrench, Brush, DoorOpen, RefreshCw, History, Timer } from "lucide-react";

const STATUSES = [
  { key: "ready", label: "Ready", icon: <Sparkles className="w-4 h-4" />, color: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  { key: "dirty", label: "Dirty", icon: <Bed className="w-4 h-4" />, color: "bg-red-50 text-red-800 border-red-200", dot: "bg-red-500" },
  { key: "cleaning", label: "Cleaning", icon: <Brush className="w-4 h-4" />, color: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  { key: "maintenance", label: "Maintenance", icon: <Wrench className="w-4 h-4" />, color: "bg-slate-100 text-slate-800 border-slate-200", dot: "bg-slate-500" },
];

export default function HousekeepingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [turnover, setTurnover] = useState([]);
  const [logFilter, setLogFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/housekeeping"); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openLog = async (roomUnitId = null) => {
    setLogOpen(true);
    try {
      const params = roomUnitId ? `?room_unit_id=${roomUnitId}` : "";
      const [l, t] = await Promise.all([
        api.get(`/admin/housekeeping/log${params}`),
        api.get("/admin/housekeeping/turnover").catch(() => ({ data: [] })),
      ]);
      setLogs(l.data);
      setTurnover(t.data);
      setLogFilter(roomUnitId || "all");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (unitId, status) => {
    try {
      await api.post(`/admin/room-units/${unitId}/housekeeping-status`, { status, note: "" });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <p className="text-sm text-[color:var(--muted-text)]">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="housekeeping-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-[color:var(--deep)]">Housekeeping board</h1>
          <p className="text-sm text-[color:var(--muted-text)] mt-1">Reception hands only <b>Ready</b> rooms to arriving guests. Rooms become <b>Dirty</b> automatically at checkout.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openLog()} data-testid="hk-open-log"><History className="w-4 h-4 mr-1" /> Audit log</Button>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="hk-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STATUSES.map((s) => (
          <Card key={s.key} className={`border ${s.color}`} data-testid={`hk-count-${s.key}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />{s.icon}<p className="text-xs uppercase tracking-wider font-semibold">{s.label}</p></div>
              </div>
              <p className="mt-3 font-heading text-3xl">{data.counts[s.key] || 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {STATUSES.map((col) => (
          <div key={col.key} className="space-y-3">
            <div className={`px-3 py-2 rounded-lg border ${col.color} flex items-center gap-2 font-ui text-sm font-semibold`}>
              <span className={`w-2 h-2 rounded-full ${col.dot}`} /> {col.label} <span className="ml-auto opacity-70">{data.grouped[col.key]?.length || 0}</span>
            </div>
            <div className="space-y-2 min-h-[100px]">
              {(data.grouped[col.key] || []).map((u) => (
                <Card key={u.id} data-testid={`hk-unit-${u.room_number}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-heading text-lg text-[color:var(--deep)]">Room {u.room_number}</p>
                        <p className="text-xs text-[color:var(--muted-text)]">{u.room_type_name}{u.floor ? ` · Floor ${u.floor}` : ""}</p>
                      </div>
                      {u.current_guest && (
                        <Badge className="bg-blue-100 text-blue-700 shrink-0">
                          <DoorOpen className="w-3 h-3 mr-1" /> occupied
                        </Badge>
                      )}
                    </div>
                    {u.current_guest && (
                      <p className="text-xs text-[color:var(--muted-text)] mt-2">
                        {u.current_guest} · until {u.current_check_out_date}
                      </p>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <Select value={u.housekeeping_status} onValueChange={(v) => setStatus(u.id, v)}>
                        <SelectTrigger data-testid={`hk-select-${u.room_number}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button onClick={() => openLog(u.id)} data-testid={`hk-log-${u.room_number}`} title="View history" className="p-1.5 rounded hover:bg-[color:var(--sand)] text-[color:var(--muted-text)] hover:text-[color:var(--brand)]">
                        <History className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {(data.grouped[col.key] || []).length === 0 && <p className="text-xs text-center text-[color:var(--muted-text)] py-6">— empty —</p>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="hk-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-[color:var(--brand)]" /> Housekeeping audit log {logFilter !== "all" && logs[0] && ` — Room ${logs[0].room_number}`}</DialogTitle>
            <DialogDescription>Every status change with who did it and when. Turnover shows avg minutes dirty → ready over the last 30 days.</DialogDescription>
          </DialogHeader>

          {turnover.length > 0 && (
            <div className="mb-4">
              <p className="text-xs uppercase tracking-wider text-[color:var(--muted-text)] mb-2 flex items-center gap-1"><Timer className="w-3.5 h-3.5" /> Turnover — avg dirty → ready (last 30d)</p>
              <div className="flex flex-wrap gap-2">
                {turnover.map((t) => (
                  <Badge key={t.room_number} variant="outline" className="text-xs" data-testid={`turnover-${t.room_number}`}>
                    Room {t.room_number}: {t.avg_minutes} min · {t.turnovers} turns
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Room</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => {
                const toColor = l.to_status === "ready" ? "bg-emerald-100 text-emerald-700" :
                                l.to_status === "dirty" ? "bg-red-100 text-red-700" :
                                l.to_status === "cleaning" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700";
                return (
                  <TableRow key={l.id} data-testid={`log-row-${l.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{formatDateTime(l.changed_at)}</TableCell>
                    <TableCell className="font-mono text-sm">{l.room_number}</TableCell>
                    <TableCell className="text-xs capitalize">{l.from_status}</TableCell>
                    <TableCell><Badge className={`${toColor} capitalize`}>{l.to_status}</Badge></TableCell>
                    <TableCell className="text-xs">{l.changed_by}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{l.source || "manual"}</Badge></TableCell>
                    <TableCell className="text-xs text-[color:var(--muted-text)] max-w-xs truncate">{l.note || "—"}</TableCell>
                  </TableRow>
                );
              })}
              {logs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-[color:var(--muted-text)]">No history yet — change a room&apos;s status to start tracking.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
